import React, { useState } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, MessageSquare, Phone } from "lucide-react";
import SitePageLayout from "../components/SitePageLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const formSchema = z.object({
  name: z.string().min(2, "Ingresa tu nombre completo"),
  email: z.string().email("Ingresa un correo valido"),
  phone: z
    .string()
    .max(20, "El telefono no puede superar 20 caracteres")
    .refine((value) => value.trim() === "" || value.trim().length >= 7, {
      message: "Ingresa un telefono valido o dejalo vacio",
    }),
  subject: z.string().min(5, "Indica el motivo de tu mensaje"),
  message: z.string().min(10, "Describe tu solicitud con mas detalle"),
});

const contactItems = [
  {
    icon: Mail,
    title: "Correo",
    value: "contacto@apedir.com",
    note: "Respondemos solicitudes comerciales, soporte y temas de privacidad.",
  },
  {
    icon: Phone,
    title: "Telefono",
    value: "+57 3226835194",
    note: "Tambien puedes dejarnos tu numero y te devolvemos la llamada.",
  },
  {
    icon: MapPin,
    title: "Ubicacion",
    value: "La Ceja, Antioquia",
    note: "Atendemos usuarios y aliados desde Colombia.",
  },
];

const ContactPage = ({
  eyebrow = "Contacto",
  title = "Contacto",
  description = "Si necesitas ayuda, soporte para la app, informacion comercial o quieres ejercer tus derechos sobre tus datos, puedes escribirnos desde este formulario.",
  highlightTitle = "Estamos para ayudarte",
  highlightDescription = "Escribenos si necesitas soporte, tienes preguntas sobre privacidad o quieres hablar de alianzas con Apedir. Nuestro equipo revisa cada mensaje y responde por el canal que nos indiques.",
  successTitle = "Mensaje enviado",
  successDescription = "Gracias por escribirnos. Usaremos los datos del formulario para responder tu solicitud.",
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        ...data,
        phone: data.phone.trim() || undefined,
      });

      setIsSuccess(true);
      toast.success("Mensaje enviado. Te responderemos lo antes posible.");
      reset();
      setTimeout(() => setIsSuccess(false), 5000);
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("No pudimos enviar tu mensaje. Intenta de nuevo.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SitePageLayout
      eyebrow={eyebrow}
      title={title}
      description={description}
    >
      <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-10 items-start">
        <div className="space-y-6">
          {contactItems.map((item) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              className="neumorphic rounded-3xl p-6"
            >
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <item.icon className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{item.title}</h2>
                  <p className="mt-1 text-lg font-semibold text-foreground">{item.value}</p>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.note}</p>
                </div>
              </div>
            </motion.div>
          ))}

          <div className="rounded-3xl border border-border bg-white/70 p-6">
            <div className="flex items-center gap-3">
              <MessageSquare className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-bold">{highlightTitle}</h3>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              {highlightDescription}
            </p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="form-container neumorphic rounded-3xl p-8"
        >
          {isSuccess ? (
            <div className="rounded-2xl bg-green-50 p-6 text-green-800">
              <h2 className="text-2xl font-bold">{successTitle}</h2>
              <p className="mt-2 text-sm leading-relaxed">{successDescription}</p>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre</Label>
                  <Input id="name" placeholder="Tu nombre" {...register("name")} />
                  {errors.name ? <p className="text-sm text-destructive">{errors.name.message}</p> : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Correo</Label>
                  <Input id="email" type="email" placeholder="tu@correo.com" {...register("email")} />
                  {errors.email ? <p className="text-sm text-destructive">{errors.email.message}</p> : null}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefono</Label>
                  <Input id="phone" type="tel" placeholder="+57 3226835194" {...register("phone")} />
                  {errors.phone ? <p className="text-sm text-destructive">{errors.phone.message}</p> : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject">Asunto</Label>
                  <Input id="subject" placeholder="Soporte, privacidad, app..." {...register("subject")} />
                  {errors.subject ? <p className="text-sm text-destructive">{errors.subject.message}</p> : null}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Mensaje</Label>
                <Textarea
                  id="message"
                  placeholder="Cuentanos en que podemos ayudarte."
                  className="min-h-[160px]"
                  {...register("message")}
                />
                {errors.message ? <p className="text-sm text-destructive">{errors.message.message}</p> : null}
              </div>

              <div className="rounded-2xl bg-muted/60 p-4 text-sm text-muted-foreground">
                Al enviar este formulario aceptas que usemos tus datos para responder tu solicitud de soporte o contacto.
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-full bg-primary py-6 text-base font-bold text-white hover:bg-primary/90"
              >
                {isSubmitting ? "Enviando..." : "Enviar mensaje"}
              </Button>
            </form>
          )}
        </motion.div>
      </div>
    </SitePageLayout>
  );
};

export default ContactPage;
